/**
 * ComposeModal — 写邮件对话框
 * 复用后端 POST /api/v1/compose/send（账号级发信，校验本地邮箱身份）。
 * 支持从邮件详情预填：回复（to=原from, subject=Re:, 引用原文）/ 转发（subject=Fwd:, 原文）。
 */
import { useEffect, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import {
  makeStyles,
  Input,
  Textarea,
  Field,
  Button,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogContent,
  DialogTitle,
  DialogActions,
} from '@fluentui/react-components'
import { SendRegular } from '@fluentui/react-icons'
import { composeApi } from '@/api/compose'
import { toast } from '@/lib/toast'
import { ApiError } from '@/api/client'

const useStyles = makeStyles({
  surface: { width: '680px', maxWidth: '92vw' },
  row: { display: 'flex', flexDirection: 'column', gap: '14px', paddingTop: '4px' },
  body: { minHeight: '240px', fontFamily: 'inherit', lineHeight: '1.6' },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: '8px', paddingTop: '8px' },
})

export interface ComposeModalProps {
  open: boolean
  onClose: () => void
  /** 默认发件人（通常为当前查看的邮箱账号） */
  defaultFrom: string
  defaultTo?: string
  defaultSubject?: string
  defaultBody?: string
  /** 标题：写邮件 / 回复 / 转发 */
  title?: string
}

export default function ComposeModal({
  open,
  onClose,
  defaultFrom,
  defaultTo = '',
  defaultSubject = '',
  defaultBody = '',
  title = '写邮件',
}: ComposeModalProps) {
  const styles = useStyles()
  const [from, setFrom] = useState(defaultFrom)
  const [to, setTo] = useState(defaultTo)
  const [subject, setSubject] = useState(defaultSubject)
  const [body, setBody] = useState(defaultBody)

  useEffect(() => {
    if (open) {
      setFrom(defaultFrom)
      setTo(defaultTo)
      setSubject(defaultSubject)
      setBody(defaultBody)
    }
  }, [open, defaultFrom, defaultTo, defaultSubject, defaultBody])

  const sendMutation = useMutation({
    mutationFn: () =>
      composeApi.send({
        from: from.trim(),
        recipients: to
          .split(/[,;]/)
          .map((s) => s.trim())
          .filter(Boolean),
        subject: subject.trim(),
        body_text: body,
      }),
    onSuccess: (res) => {
      if (res.failed > 0) {
        toast.warning('部分发送失败', `${res.sent}/${res.total} 成功，${res.failed} 失败`)
      } else {
        toast.success('已发送', `成功发送给 ${res.sent} 个收件人`)
      }
      onClose()
    },
    onError: (err) => toast.error('发送失败', err instanceof ApiError ? err.message : '请检查输入'),
  })

  if (!open) return null

  return (
    <Dialog open={open} onOpenChange={(_, d) => !d.open && onClose()} modalType="modal">
      <DialogSurface className={styles.surface}>
        <DialogBody>
          <DialogTitle>{title}</DialogTitle>
          <DialogContent>
            <div className={styles.row}>
              <Field label="发件人">
                <Input value={from} onChange={(_, d) => setFrom(d.value)} placeholder="you@yourdomain.com" />
              </Field>
              <Field label="收件人">
                <Input
                  value={to}
                  onChange={(_, d) => setTo(d.value)}
                  placeholder="多个收件人用逗号或分号分隔"
                />
              </Field>
              <Field label="主题">
                <Input value={subject} onChange={(_, d) => setSubject(d.value)} />
              </Field>
              <Field label="正文">
                <Textarea className={styles.body} value={body} onChange={(_, d) => setBody(d.value)} />
              </Field>
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="subtle" onClick={onClose}>
              取消
            </Button>
            <Button
              appearance="primary"
              icon={<SendRegular />}
              onClick={() => sendMutation.mutate()}
              disabled={sendMutation.isPending || !to.trim() || !from.trim()}
            >
              {sendMutation.isPending ? '发送中…' : '发送'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  )
}
